import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { apiCacheKey } from '../utils/storageKeys';

const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Extract the items array from any API response shape.
 * Used by input/dropdown components that need a flat list for rendering options.
 *   - flat array                              → returned as-is
 *   - { count, next, previous, results }      → returns results
 *   - { status, data: [...] }                 → returns data
 *   - { status, data: { count, …, results } } → returns data.results
 */
function extractItems(raw) {
  if (!raw) return raw;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.results)) return raw.results;
  if (raw.data !== undefined) {
    if (Array.isArray(raw.data)) return raw.data;
    if (raw.data && Array.isArray(raw.data.results)) return raw.data.results;
  }
  // Every consumer of this hook renders/searches the result as a flat list
  // (dropdown options, .find/.map lookups) — an unrecognized non-array shape
  // (e.g. an error envelope) must not leak through and break those call sites.
  return [];
}

/**
 * Fetches a URL with sessionStorage caching.
 * - Returns cached data immediately if it is fresh (< TTL).
 * - Falls back to a network fetch when the cache is stale or missing.
 * - The cache is sessionStorage-backed: it clears when the tab closes.
 * - Automatically unwraps paginated envelopes to a flat array so dropdown
 *   and input components don't need to handle the pagination shape.
 *
 * @param {string}  url      - Full API URL to fetch
 * @param {object}  headers  - Request headers (e.g. auth token)
 * @param {object}  options
 * @param {boolean} options.skip  - Set true to skip fetching entirely
 * @param {number}  options.ttl   - Cache lifetime in ms (default 5 min)
 *
 * @returns {{ data, loading, error, invalidate }}
 */
export const useApiCache = (url, headers = {}, options = {}) => {
  const { skip = false, ttl = DEFAULT_TTL_MS } = options;
  const cacheKey = apiCacheKey(url);
  const mounted = useRef(true);

  const [state, setState] = useState(() => {
    if (skip || !url) return { data: null, loading: false, error: null };
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const { data, cachedAt } = JSON.parse(raw);
        if (Date.now() - cachedAt < ttl) {
          return { data, loading: false, error: null };
        }
      }
    } catch {}
    return { data: null, loading: true, error: null };
  });

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (skip || !url) return;

    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const { data, cachedAt } = JSON.parse(raw);
        if (Date.now() - cachedAt < ttl) {
          if (mounted.current) setState(s => ({ ...s, loading: false }));
          return;
        }
      }
    } catch {}

    setState(s => ({ ...s, loading: true, error: null }));
    axios
      .get(url, { headers })
      .then((res) => {
        const data = extractItems(res.data);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ data, cachedAt: Date.now() }));
        } catch {}
        if (mounted.current) setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (mounted.current) setState(s => ({ ...s, loading: false, error: err }));
      });
  }, [url, skip, ttl]); // eslint-disable-line react-hooks/exhaustive-deps

  const invalidate = () => {
    try { sessionStorage.removeItem(cacheKey); } catch {}
    if (!skip && url) setState({ data: null, loading: true, error: null });
  };

  return { ...state, invalidate };
};
